import { inject, Injectable } from '@angular/core';
import {
  AccountCreationResponse,
  AccountCreationResponses,
  AccountCreationSteps,
  CoreAccountsInterface,
  FinancialAccountDetails,
  FinancialAccountsCoreService,
  FinancialAccountsCreationService,
  FinancialAccountsHistoryService,
  FinancialAccountTransaction,
  FinancialAccountTransactionsRequestConfig,
  FinancialAccountTypes,
  TransactionPagination,
} from '@brightside-web/desktop/feature/financial-accounts-core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { GenericResponseCode } from '@brightside-web/desktop/data-access/core-services';
import { HomeFeedSection, HomeFeedService } from '@brightside-web/desktop/feature/home-feed';
import { ServiceInventoryService, ServiceState } from '@brightside-web/desktop/data-access/service-inventory';
import { SavingsInitialCardComponent } from './home-cards/savings-initial-card/savings-initial-card.component';
import { SavingsCompleteCardComponent } from './home-cards/savings-complete-card/savings-complete-card.component';

export interface ArrayOfSavingsAccounts {
  accounts: SavingsAccount[];
}

export interface SingleSavingsAccount {
  account: SavingsAccount;
}

export interface SavingsAccount extends CoreAccountsInterface {
  accountType: FinancialAccountTypes.SAVINGS;
}

export type SavingsAccountState = SavingsAccount | null | undefined;

@Injectable({ providedIn: 'root' })
export class NewSavingsAccountService {

  private _serviceInventory = inject(ServiceInventoryService);

  private _storedAccount: SavingsAccount | undefined;

  private _currentAccountInfo = new BehaviorSubject<SavingsAccountState>(undefined);
  public readonly currentAccountInfo: Observable<SavingsAccountState> = this._currentAccountInfo.asObservable();

  private _accountCreation = new Subject<AccountCreationResponse>();
  public readonly accountCreation: Observable<AccountCreationResponse> = this._accountCreation.asObservable();

  public readonly recentTransactions: Observable<FinancialAccountTransaction[] | undefined> =
    this.faHistorySvc.recentTransactions;
  public readonly transactionHistory: Observable<TransactionPagination | undefined> = this.faHistorySvc.transactionHistory;
  public readonly hasMoreTransactions: Observable<boolean> = this.faHistorySvc.hasMoreTransactions;

  private _accountDetails = new BehaviorSubject<FinancialAccountDetails | undefined>(undefined);
  public readonly accountDetails: Observable<FinancialAccountDetails | undefined> = this._accountDetails.asObservable();

  private _serviceState = new BehaviorSubject<string | undefined>(undefined);
  public readonly serviceState: Observable<string | undefined> = this._serviceState.asObservable();


  constructor(
    private faCoreServices: FinancialAccountsCoreService,
    private faCreateSSvc: FinancialAccountsCreationService,
    private faHistorySvc: FinancialAccountsHistoryService
  ) {}

  getSavingsAccounts(force = false) {
    if (force) this.clearStoredSavings();
    if (!this._storedAccount) {
      this.faCoreServices.getAccountsByType<ArrayOfSavingsAccounts>(FinancialAccountTypes.SAVINGS)
        .subscribe({
          next: (resp) => {
              if (resp.payload && resp.payload.accounts.length > 0) {
                this._currentAccountInfo.next(resp.payload.accounts[0]);
                this._storedAccount = resp.payload.accounts[0];
              } else {
                this._currentAccountInfo.next(null);
              }
          },
          error: err => {
            this._serviceState.next('error');
          }
        })
    } else {
      setTimeout(() => {
        this._currentAccountInfo.next(this._storedAccount);
      }, 0);
    }
  }

  clearStoredSavings() {
    this._storedAccount = undefined;
    this._currentAccountInfo.next(undefined);
  }

  getSavingsById(id: number) {
    this.faCoreServices.getAccountById<SingleSavingsAccount>(FinancialAccountTypes.SAVINGS, id);
  }

  getAccountDetails() {
    if (this._storedAccount && !this._accountDetails.value) {
      this.faCoreServices.getAccountDetails(FinancialAccountTypes.SAVINGS, this._storedAccount.accountId).subscribe((details) => {
        if (details.result.code === GenericResponseCode.SERVICE_OK) {
          this._accountDetails.next(details.payload);
        }
      });
    }
  }

  getSavingsTransactions(callParams?: FinancialAccountTransactionsRequestConfig) {
    if (this._storedAccount) {
      this.faHistorySvc.getTransactions(FinancialAccountTypes.SAVINGS, this._storedAccount?.accountId);
    } else {
      this.currentAccountInfo.pipe(take(1)).subscribe((account) => {
        if (account) this.faHistorySvc.getTransactions(FinancialAccountTypes.SAVINGS, account.accountId);
      });
      this.getSavingsAccounts();
    }
  }

  getNextSavingsTransactionsPage() {
    if (this._storedAccount) {
      this.faHistorySvc.getNextTransactionsPage(FinancialAccountTypes.SAVINGS, this._storedAccount?.accountId);
    }
  }

  createSavingsAccount() {
    this.faCreateSSvc.accountCreatedResp.pipe(take(1)).subscribe((resp) => {
      this._accountCreation.next(resp as AccountCreationResponse);
      this.createSavingsAccount();
    });

    this.faCreateSSvc
      .createAccount<SingleSavingsAccount>(FinancialAccountTypes.SAVINGS)
      .pipe(
        tap((resp) => {
          if (
            resp.result.code === GenericResponseCode.SERVICE_OK ||
            resp.result.code === AccountCreationResponses.ACCOUNT_ALREADY_EXISTS
          ) {
            this._currentAccountInfo.next(resp.payload.account);
            this._storedAccount = resp.payload.account;
            this.onSuccess();
          }
        }),
        take(1)
      )
      .subscribe((resp) => {
        this._accountCreation.next(resp.result.code as AccountCreationResponse);
      });
  }

  onSuccess() {
    this.faCreateSSvc.advanceStep(AccountCreationSteps.CREATE);
  }


}
